/**
 * Lectura del rastro de auditoría de informes (Trazabilidad → Auditoría).
 *
 * La tabla vive en el esquema `auditoria`, cerrado a la clave anon: el
 * frontend NO puede consultarla con supabaseClient. Todo pasa por
 * `/api/pdf/audit-log`, que valida el JWT de Clerk y exige rol admin.
 *
 * El token se pasa por parámetro (`getToken` de `useAuth`) en vez de importar
 * Clerk aquí, para que el servicio siga siendo testeable sin contexto de React.
 */

/** Tipos de informe que audita el sistema (coinciden con el CHECK de la tabla). */
export const TIPOS_INFORME = ['trazabilidad', 'certificado', 'cotizacion'];

/**
 * Trae una página del rastro de auditoría.
 *
 * @param {() => Promise<string|null>} getToken  `getToken` de `useAuth()` de Clerk
 * @param {object} [filtros]
 * @param {number} [filtros.limit=50]
 * @param {number} [filtros.offset=0]
 * @param {string} [filtros.tipo]     trazabilidad | certificado | cotizacion
 * @param {string} [filtros.clerkId]  filtra por usuario del admin
 * @param {string} [filtros.desde]    ISO date
 * @param {string} [filtros.hasta]    ISO date
 * @returns {Promise<{ registros: object[], total: number }>}
 * @throws {Error} con `.status` (401/403/500) para que la UI distinga el caso
 */
export async function fetchAuditLog(getToken, filtros = {}) {
  const token = await getToken({ skipCache: true });
  if (!token) {
    const err = new Error('Sesión no válida');
    err.status = 401;
    throw err;
  }

  const params = new URLSearchParams();
  params.set('limit',  String(filtros.limit  ?? 50));
  params.set('offset', String(filtros.offset ?? 0));
  if (filtros.tipo)    params.set('tipo', filtros.tipo);
  if (filtros.clerkId) params.set('clerkId', filtros.clerkId);
  if (filtros.desde)   params.set('desde', filtros.desde);
  if (filtros.hasta)   params.set('hasta', filtros.hasta);

  const res = await fetch(`/api/pdf/audit-log?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    // El backend responde {error}; si el body no es JSON, se usa el statusText.
    const detalle = await res.json().catch(() => ({}));
    const err = new Error(detalle.error || res.statusText || 'Error al leer la auditoría');
    err.status = res.status;
    throw err;
  }

  return res.json();
}
